import React, { useEffect, useState } from "react";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useNavigate } from "react-router-dom";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import useHost from "../useHooks";
import { useSelector } from "react-redux";
import Pagination from "components/pagination";
import useStore from "hooks/useStore";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

const MyBadges: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "My Badges", path: siteRoutes.myBadges },
  ];

  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();
  const [data, setData] = useState<BadgeDTO[]>([]);
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
  });
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { getAwardedBadges } = useHost();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const loadBadges = (page = 0, size = 10) => {
    const queryParams = {
      page,
      size,
      badgeType: "achievement",
    };
    getAwardedBadges(setData, queryParams, setFilters);
  };

  const handlePageChange = ({
    selected,
    limit,
  }: {
    selected: number;
    limit: number;
  }) => {
    loadBadges(selected, limit);
  };

  useEffect(() => {
    loadBadges();
  }, []);

  // Mark initial load as complete when loading finishes
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  return (
    <div className="container min-h-[70vh]">
      <Breadcrumb links={breadcrumbLinks} />
      <div className="w-full flex justify-between gap-4">
        <h2 className="text-2xl font-bold mb-4 text-blackie">My Badges</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {data.length > 0
          ? data.map((badge, index) => (
              <div
                key={index}
                className={`relative flex flex-col items-center justify-center p-6 cursor-pointer border rounded-[8px]`}
              >
                <img
                  className="w-[98px] min-h-[108px] object-cover transition-transform duration-200 hover:scale-105"
                  src={badge.image?.name || badge.imageUrl}
                  alt={badge.badgeType}
                />
                <span className="text-lg font-semibold mt-3 text-black capitalize">
                  {badge.badgeType}
                </span>
              </div>
            ))
          : null}
      </div>

      <CustomMessageDisplay
        show={!isLoading && !data.length && !isInitialLoad}
        title="No Badges Available"
        slogan="You haven't earned any badges yet. Keep participating in experiences to earn achievement badges!"
        image={noFoundGif}
        imageAlt="No badges found"
        className="py-12"
        imageClassName="w-24 h-24"
        titleClassName="text-2xl font-bold text-blackie"
        sloganClassName="text-gray-500 text-base"
      />

      {data.length > 0 && filters.totalElements > filters.size && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
    </div>
  );
};

export default MyBadges;
